import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';

interface Party {
  id: number;
  title: string;
  date: string;
  location: string | null;
  description: string | null;
  imageUrl: string | null;
  createdAt: string;
}

export default function PartyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [party, setParty] = useState<Party | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchParty();
  }, [id, user, navigate]);

  const fetchParty = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<Party>(`/api/parties/${id}`);
      setParty(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load party');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="page-loading">Loading party...</div>;
  if (error) return <div className="page-loading">{error}</div>;
  if (!party) return <div className="page-loading">Party not found</div>;

  return (
    <div className="page-container">
      <button
        onClick={() => navigate('/parties')}
        className="btn btn-outline"
        style={{ marginBottom: '1.5rem' }}
      >
        ← Tilbage til fester
      </button>

      <div className="party-detail card">
        {party.imageUrl && (
          <img src={party.imageUrl} alt={party.title} className="party-detail-image" />
        )}

        <div className="party-detail-body">
          <h1 className="party-detail-title">{party.title}</h1>

          <div className="party-detail-meta">
            <div className="party-meta-item">
              <span className="party-meta-label">📅 Dato & Tid:</span>
              <span className="party-meta-value">
                {new Date(party.date).toLocaleDateString('da-DK', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>

            {party.location && (
              <div className="party-meta-item">
                <span className="party-meta-label">📍 Lokation:</span>
                <span className="party-meta-value">{party.location}</span>
              </div>
            )}

            <div className="party-meta-item">
              <span className="party-meta-label">📝 Oprettet:</span>
              <span className="party-meta-value">
                {new Date(party.createdAt).toLocaleDateString('da-DK')}
              </span>
            </div>
          </div>

          {party.description && (
            <div className="party-detail-description">
              <h2>Beskrivelse</h2>
              <p>{party.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
